import CalendarIcon from "@assets/icons/calendar.svg";
import { useI18n } from "@core/i18n/i18n";
import { patientProgramsManager, onboardingManager } from "@core/service-container";
import { useFormatPatients } from "@domain/patient/hooks/use-patients";
import { Patient, PendingPatient } from "@domain/patient/patient";
import { isNewProgram, NewProgram, Program, ProgramContent } from "@domain/program/program";
import { sendAnalyticsEventDateModification } from "@domain/program/program-analytics";
import { ProgramStatus } from "@domain/program/program-status/program-status";
import { e2eTestId } from "@e2e/test-ids";
import styled from "@emotion/styled";
import { AlertCard, CardType } from "@ui/components/common/alert-card";
import { Button, ButtonProps } from "@ui/components/buttons/button";
import { BaseDialog, DIALOG_SIZE } from "@ui/components/dialog/base-dialog";
import { ErrorMessage } from "@ui/components/common/error-message";
import { Tooltip } from "@ui/components/common/tooltip";
import { OnboardingPanel } from "@ui/components/onboarding/onboarding-panel";
import { OnboardingProgramQuickstartStep } from "@domain/onboarding/onboarding";
import { clearStartGuide02Request, startGuide02Requested } from "@ui/pages/program-builder/quickstart/start-guide-signal";
import { sendAnalyticsEventViewGuide } from "@domain/program/program-analytics";
import { useObservable } from "micro-observables";
import { useScheduleShortcuts } from "@ui/pages/program-builder/dialog/schedule-dialog/use-schedule-shortcuts";
import { useAllPatientPrograms } from "@ui/pages/program-builder/hooks/use-patient-program";
import { physioMeMobileBreakpoint, row, stack } from "@ui/style";
import { colors } from "@ui/style/colors";
import { isNil, isNotNil } from "@utils/assert";
import { computeDiffInDays, dateIsBeforeOrSameAs, now, nowAsStartOfDay, TWO_MONTH_IN_DAYS } from "@utils/date-utils";
import { positiveModulo } from "@utils/number-utils";
import dayjs, { Dayjs } from "dayjs";
import { AnimatePresence, motion } from "framer-motion";
import _ from "lodash";
import { Calendar } from "primereact/calendar";
import { DialogProps } from "primereact/dialog";
import { classNames } from "primereact/utils";
import React, { useCallback, useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";

export interface ScheduleProgramFormData {
	startDate: Dayjs | undefined;
	endDate: Dayjs | undefined;
}

export interface ScheduleProgramDialogProps extends DialogProps {
	patient: Patient | PendingPatient;
	editedProgram: ProgramContent;
	program: Program | NewProgram;
	onSave: (dates: ScheduleProgramFormData) => void;
	secondaryButtonProps?: Omit<ButtonProps, "onClick"> & { onClick: (dates: ScheduleProgramFormData) => void };
}

const MAX_PROGRAM_MONTH_DURATION = 24;

const CalendarFooterContent: React.FC<{
	text: string;
}> = ({ text }) => <CalendarFooter className="p-size-xs-semi-bold">{text}</CalendarFooter>;

export const ScheduleProgramDialog: React.FC<ScheduleProgramDialogProps> = ({
	program,
	editedProgram,
	onSave,
	patient,
	secondaryButtonProps,
	...otherProps
}) => {
	const { format, formatDateShort, formatDateMed, formatProgramDuration, getEndWeekDay } = useI18n();
	const { formatPatientName } = useFormatPatients();

	const disabledWeekDays = _.without(_.range(7), positiveModulo(dayjs.localeData().firstDayOfWeek() - 1, 7));

	const maxDate = useMemo(() => now().add(MAX_PROGRAM_MONTH_DURATION, "month").endOf("week"), []);

	const { allPatientPrograms, shouldDisplayProgramEndingSoonAlert } = useAllPatientPrograms(patient);

	const getDefaultStartDate: () => Dayjs = useCallback(() => {
		switch (program.status) {
			case ProgramStatus.Ended:
			case ProgramStatus.Ongoing:
				return editedProgram.startDate as Dayjs;
			case ProgramStatus.Scheduled:
				return isNotNil(editedProgram.startDate) && dateIsBeforeOrSameAs(now(), editedProgram.startDate)
					? editedProgram.startDate
					: now();
			case ProgramStatus.Draft: {
				const lastScheduledProgram = _.maxBy(allPatientPrograms?.scheduledPrograms, p => p.endDate.valueOf());
				if (isNotNil(lastScheduledProgram)) {
					return lastScheduledProgram.endDate.add(1, "day");
				}
				return isNotNil(allPatientPrograms?.ongoingProgram?.endDate)
					? allPatientPrograms.ongoingProgram.endDate.add(1, "day")
					: now();
			}
		}
	}, [allPatientPrograms?.ongoingProgram, allPatientPrograms?.scheduledPrograms, editedProgram.startDate, program.status]);

	const { control, handleSubmit, setValue, watch } = useForm<ScheduleProgramFormData>({
		defaultValues: {
			startDate: getDefaultStartDate(),
			endDate: editedProgram.endDate,
		},
		shouldFocusError: false,
	});

	useEffect(() => {
		setValue("startDate", getDefaultStartDate());
	}, [getDefaultStartDate, setValue]);

	const guide02Visible = useObservable(startGuide02Requested);
	useEffect(() => () => clearStartGuide02Request(), []);

	useEffect(() => {
		if (guide02Visible) {
			sendAnalyticsEventViewGuide(OnboardingProgramQuickstartStep.START_GUIDE_02);
		}
	}, [guide02Visible]);

		const scheduleDates = async (dates: ScheduleProgramFormData) => {
		if (isNil(dates.startDate)) {
			throw new Error();
		}
		onSave(dates);
		otherProps.onHide();
		sendAnalyticsEventDateModification(patient.id, program, dates);
		if (program.status === ProgramStatus.Draft) {
			try {
				await onboardingManager.passStepOnboarding(OnboardingProgramQuickstartStep.START_GUIDE_02);
			} catch {
				// ignore onboarding errors
			}
			clearStartGuide02Request();
		}
	};

	const startDate = watch("startDate");
	const endDate = watch("endDate");

	const overlappedProgram = useMemo(
		() =>
			isNotNil(endDate) &&
			isNotNil(startDate) &&
			patientProgramsManager.getOverlappingProgram(
				patient.id,
				startDate,
				endDate,
				isNewProgram(program) ? undefined : program.id,
			),
		[patient, startDate, endDate, program],
	);

	const setEndDate = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, date: Dayjs) => {
		event.preventDefault();
		const clampedEndDate = date.isAfter(maxDate) ? maxDate : date;
		setValue("endDate", clampedEndDate);
	};

	const endingSoonRemainingDays = useMemo(() => {
		const days = isNotNil(endDate) ? computeDiffInDays(nowAsStartOfDay(), endDate) : undefined;
		return shouldDisplayProgramEndingSoonAlert && isNotNil(days) && days < TWO_MONTH_IN_DAYS ? days : undefined;
	}, [endDate, shouldDisplayProgramEndingSoonAlert]);

	const renderSecondaryButton = useCallback(
		() =>
			isNotNil(secondaryButtonProps) ? (
				<ResponsiveButton
					uiStyle="secondary"
					{...secondaryButtonProps}
					onClick={() => {
						otherProps.onHide();
						secondaryButtonProps.onClick({ startDate, endDate });
					}}
				/>
			) : null,
		[endDate, otherProps, secondaryButtonProps, startDate],
	);

	const durationIsInvalid = isNil(endDate) || endDate.isBefore(startDate) || !!overlappedProgram;
	const endWeekDay = getEndWeekDay();
	const calendarFooterText = format("programBuilder.scheduleDialog.calendarFooter", { endDay: endWeekDay });
	const renderCalendarFooter = useCallback(
		() => <CalendarFooterContent text={calendarFooterText} />,
		[calendarFooterText],
	);

	return (
		<BaseDialog
			header={format("programBuilder.scheduleDialog.header")}
			footer={
				<ButtonContainer>
					{renderSecondaryButton()}
					<ResponsiveButton
						type="submit"
						onClick={handleSubmit(data => scheduleDates(data))}
						label={format(
							program.status === ProgramStatus.Draft
								? "programBuilder.scheduleDialog.startButton"
								: "programBuilder.scheduleDialog.okButton",
						)}
						data-test={e2eTestId.scheduleProgramDialog.submitButton}
					/>
				</ButtonContainer>
			}
			width={DIALOG_SIZE.S}
			closeOnEscape
			dismissableMask
			{...otherProps}
		>
			<DialogContainer>
				<Form>
					<FormContainer>
						<AnimatePresence>
							{overlappedProgram && (
								<MotionWarnCard
									message={format("programBuilder.startDialog.overlapWarn.v1_6", {
										name: formatPatientName(patient),
										startDate: formatDateMed(overlappedProgram.startDate),
										endDate: formatDateMed(overlappedProgram.endDate),
									})}
									initial={{ opacity: 0 }}
									animate={{ opacity: 1 }}
									exit={{ opacity: 0 }}
									cardType={CardType.Warn}
								/>
							)}
							{!overlappedProgram && isNotNil(endingSoonRemainingDays) && (
								<MotionWarnCard
									title={format("programBuilder.scheduleDialog.endingSoonAlert.title", {
										remainingDays: endingSoonRemainingDays,
									})}
									message={format("programBuilder.scheduleDialog.endingSoonAlert.message", {
										patientName: patient.firstName,
									})}
									initial={{ opacity: 0 }}
									animate={{ opacity: 1 }}
									exit={{ opacity: 0 }}
									cardType={CardType.Warn}
								/>
							)}
						</AnimatePresence>
						<FormField>
							{guide02Visible && (
								<StyledOnboardingPanel
									text={format("programBuilder.quickstart.step.startGuide02")}
									onClose={() => {
										clearStartGuide02Request();
									}}
								/>
							)}
							<span className="p-size-m">{format("programBuilder.startDialog.startingDate.label")}</span>
							<Controller
								name="startDate"
								control={control}
								rules={{
									required: format("programBuilder.startDialog.startingDate.required"),
									validate: () =>
										program.status !== ProgramStatus.Ongoing && overlappedProgram
											? format("programBuilder.startDialog.startingDate.invalid")
											: undefined,
								}}
								render={({ field, fieldState }) => (
									<>
										<StyledCalendar
											{...field}
											value={field.value?.toDate()}
											disabled={program.status === ProgramStatus.Ongoing}
											onChange={e => setValue("startDate", isNotNil(e.value) ? dayjs(e.value) : undefined)}
											className={classNames({ "p-invalid": fieldState.invalid && !!overlappedProgram })}
											minDate={new Date()}
											inputStyle={{ width: 100 }}
											formatDateTime={() => formatDateShort(startDate)}
											icon={<CalendarIcon height={24} width={24} />}
											showIcon
										/>
										<ErrorMessage>{overlappedProgram && fieldState.error?.message}</ErrorMessage>
									</>
								)}
							/>
						</FormField>
						<FormField>
							<label className="p-size-m">{format("programBuilder.scheduleDialog.endDate.label")}</label>
							<DurationShortcuts
								programStatus={program.status}
								startDate={startDate}
								endDate={endDate}
								maxDate={maxDate}
								onClick={setEndDate}
							/>
							<Controller
								name="endDate"
								control={control}
								rules={{
									required: format("programBuilder.scheduleDialog.endDate.required"),
									validate: () =>
										durationIsInvalid ? format("programBuilder.startDialog.startingDate.invalid") : undefined,
								}}
								render={({ field, fieldState }) => (
									<>
										<StyledCalendar
											{...field}
											value={field.value?.toDate()}
											onChange={e => setValue("endDate", isNotNil(e.value) ? dayjs(e.value) : undefined)}
											className={classNames({ "p-invalid": fieldState.invalid && !!overlappedProgram })}
											minDate={new Date()}
											maxDate={maxDate.toDate()}
											disabledDays={disabledWeekDays}
											formatDateTime={() => formatDateShort(endDate)}
											footerTemplate={renderCalendarFooter}
											placeholder={format("programBuilder.scheduleDialog.endDatePlaceholder")}
											icon={<CalendarIcon height={24} width={24} />}
											showIcon
										/>
										<ErrorMessage>{durationIsInvalid && fieldState.error?.message}</ErrorMessage>
									</>
								)}
							/>
							{isNotNil(endDate) && isNotNil(startDate) && !durationIsInvalid && (
								<Duration className="p-size-s">
									{format("programBuilder.startDialog.weeks.duration", {
										duration: formatProgramDuration(startDate, endDate),
									})}
								</Duration>
							)}
						</FormField>
					</FormContainer>
				</Form>
			</DialogContainer>
		</BaseDialog>
	);
};

const DialogContainer = styled.div`
	${stack("M", "flexstart", "stretch")}
	height: 100%;
`;

const ButtonContainer = styled.div`
	display: flex;
	gap: 24px;
	justify-content: flex-end;
	@media ${physioMeMobileBreakpoint} {
		flex-direction: column-reverse;
		gap: 12px;
	}
`;

const Form = styled(motion.form)`
	${stack("M", "spacebetween", "stretch")}
	height: 100%;
`;

const FormContainer = styled.div`
	${stack("M", "flexstart", "stretch")}
`;

const FormField = styled.div`
	${stack("S", "flexstart", "stretch")};
`;

const StyledCalendar = styled(Calendar)`
	@media ${physioMeMobileBreakpoint} {
		max-width: 100%;
	}

	.p-tooltip .p-tooltip-text {
		visibility: hidden !important;
		width: 30px !important;
	}
`;

const ResponsiveButton = styled(Button)`
	@media ${physioMeMobileBreakpoint} {
		align-self: stretch;
	}
`;

const MotionWarnCard = motion(AlertCard);

const CalendarFooter = styled.div`
	max-width: 375px;
	margin: 12px;
	color: ${colors.grey80};
`;

const Duration = styled.div`
	color: ${colors.grey80};
	margin-top: -8px;
`;

const StyledOnboardingPanel = styled(OnboardingPanel)`
	margin-bottom: 8px;
`;

const DurationShortcuts: React.FC<{
	programStatus: ProgramStatus;
	startDate: Dayjs | undefined;
	endDate: Dayjs | undefined;
	maxDate: Dayjs | undefined;
	onClick: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, newDate: Dayjs) => void;
}> = ({ programStatus, startDate, endDate, maxDate, onClick }) => {
	const shortcuts = useScheduleShortcuts(programStatus, startDate, endDate);
	const { format } = useI18n();
	const shouldDisplayTooltip = useCallback((newEndDate: Dayjs) => newEndDate.isAfter(maxDate), [maxDate]);

	return (
		<ShortcutsContainer>
			{shortcuts.map((shortcut, index) => (
				<Tooltip
					disabled={!shouldDisplayTooltip(shortcut.newDate)}
					content={format("programBuilder.scheduleDialog.endDateTooltip")}
					placement="auto"
					maxWidth={400}
					key={`${shortcut.label}-${shortcut.newDate.valueOf()}`}
				>
					<div>
						<ShortcutButton
							className="p-button-secondary ui-button-outlined"
							onClick={event => {
								onClick(event, shortcut.newDate);
								shortcut.sendAnalyticsEvent();
							}}
							label={shortcut.label}
							disabled={isNil(startDate)}
							data-test={e2eTestId.scheduleProgramDialog.shortcut(index)}
						/>
					</div>
				</Tooltip>
			))}
		</ShortcutsContainer>
	);
};

const ShortcutsContainer = styled.div`
	${row(8)}
`;

const ShortcutButton = styled(Button)`
	background-color: ${colors.white} !important;
	width: 100%;
	padding: 8px 16px;
	font-size: 14px;
`;
